import {dateTimeParse} from '@gravity-ui/date-utils';
import {ValidationType} from 'common/constants/survey';
import {CaptchaValue, FileValue, QuestionValue} from 'common/models/survey';
import i18n from 'i18n';

import {isValidDateRange} from './isDateRange';
import isEmpty from './isEmpty';
import isValidStringDate from './isValidStringDate';

const isCaptchaValue = (value: QuestionValue): value is CaptchaValue => {
    return Boolean(value) && typeof value === 'object' && 'key' in value && 'value' in value;
};

export const errorMessageByType: Record<ValidationType, string> = {
    [ValidationType.Required]: i18n('survey-page', 'validation-required'),
    [ValidationType.Decimal]: i18n('survey-page', 'validation-decimal'),
    [ValidationType.Email]: i18n('survey-page', 'validation-email'),
    [ValidationType.URL]: i18n('survey-page', 'validation-url'),
    [ValidationType.Phone]: i18n('survey-page', 'validation-phone'),
    [ValidationType.INN]: i18n('survey-page', 'validation-inn'),
    [ValidationType.Russian]: i18n('survey-page', 'validation-russian'),
    [ValidationType.RegExp]: i18n('survey-page', 'validation-regexp'),
    [ValidationType.Min]: '',
    [ValidationType.Max]: '',
    [ValidationType.Size]: '',
    [ValidationType.Count]: i18n('survey-page', 'validation-max-count-files'),
};

export const validateRequired = (input: QuestionValue) => {
    const error = errorMessageByType[ValidationType.Required];
    const value = typeof input === 'string' ? input.trim() : input;
    if (typeof value === 'boolean') {
        return value || error;
    }
    if (typeof value === 'number') {
        return true;
    }
    if (isCaptchaValue(value)) {
        return Boolean(value.key && value.value) || error;
    }
    return !isEmpty(value) || error;
};

export const validateMaxCount = (maxCount: number) => (value: QuestionValue) => {
    if (Array.isArray(value)) {
        return value.length <= maxCount || errorMessageByType[ValidationType.Count];
    }
    return true;
};

export const validateMaxSize = (maxSize: number) => (value: QuestionValue) => {
    if (Array.isArray(value)) {
        const maxSizeInBytes = maxSize * 1024 * 1024;
        const isEveryFileFitsSize = (value as FileValue[]).every(
            (file) => (file.size ?? 0) <= maxSizeInBytes,
        );

        if (isEveryFileFitsSize) {
            return true;
        }

        return i18n('survey-page', 'validation-max-size-files', {maxSize});
    }
    return true;
};

const validateDateBefore = (dateValue: string, before: string) => {
    const date = dateTimeParse(dateValue);
    return date?.isBefore(before) || date?.isSame(before);
};

const validateDateAfter = (dateValue: string, after: string) => {
    const date = dateTimeParse(dateValue);
    return dateTimeParse(after)?.isBefore(dateValue) || date?.isSame(after);
};

const validateDate = (value: QuestionValue, limit: string, type: 'max' | 'min') => {
    const validateFunc = type === 'max' ? validateDateBefore : validateDateAfter;

    let valid: boolean | undefined = false;

    if (isValidStringDate(value)) {
        valid = validateFunc(value, limit);
    } else if (isValidDateRange(value)) {
        valid = validateFunc(value.begin, limit) && validateFunc(value.end, limit);
    } else {
        return i18n('survey-page', 'validation-date');
    }

    return (
        valid ||
        i18n('survey-page', `validation-${type}-date`, {
            date: dateTimeParse(limit)?.format('DD.MM.YYYY'),
        })
    );
};

export const validateDateRange = (value: QuestionValue) => {
    if (isEmpty(value)) {
        return true;
    }
    if (isValidDateRange(value)) {
        return true;
    }
    return i18n('survey-page', 'validation-date');
};

export const validateMin = (min: number | string | boolean) => (value: QuestionValue) => {
    if (isValidStringDate(min) && !isEmpty(value)) {
        return validateDate(value, min, 'min');
    }
    if (typeof value === 'string' && value) {
        return value.length >= Number(min) || i18n('survey-page', 'validation-min-string', {min});
    }
    if (typeof value === 'number') {
        return value >= Number(min) || i18n('survey-page', 'validation-min-number', {min});
    }
    return true;
};

export const validateMax = (max: number | string | boolean) => (value: QuestionValue) => {
    if (isValidStringDate(max) && !isEmpty(value)) {
        return validateDate(value, max, 'max');
    }
    if (typeof value === 'string' && value) {
        return value.length <= Number(max) || i18n('survey-page', 'validation-max-string', {max});
    }
    if (typeof value === 'number') {
        return value <= Number(max) || i18n('survey-page', 'validation-max-number', {max});
    }
    return true;
};

export const validateDecimal = (value: QuestionValue) => {
    const error = errorMessageByType[ValidationType.Decimal];
    if (typeof value === 'string') {
        if (!value) {
            return true;
        }
        if (!isNaN(Number(value)) || !isNaN(parseFloat(value.replace(',', '.')))) {
            return true;
        }
        return error;
    }

    if (typeof value === 'number') {
        return !isNaN(value) || error;
    }

    return error;
};

export const validateRegExp = (regExpStr: string) => (value: QuestionValue) => {
    const error = errorMessageByType[ValidationType.RegExp];
    if (typeof value !== 'string') {
        return error;
    }
    if (!value) {
        return true;
    }
    const regExp = new RegExp(regExpStr);
    return regExp.test(value) || error;
};

const validateByRegExp = (type: ValidationType, regExpStr: RegExp, value: QuestionValue) => {
    const error = errorMessageByType[type];
    if (typeof value !== 'string') {
        return error;
    }
    if (!value) {
        return true;
    }
    const regex = new RegExp(regExpStr);
    return regex.test(value.trim()) || error;
};

// Taken from HTML spec: https://html.spec.whatwg.org/multipage/input.html#valid-e-mail-address
const EMAIL_REGEXP =
    // eslint-disable-next-line
    /^\S+@\S+\.\S+$/;

export const validateEmail = (value: QuestionValue) =>
    validateByRegExp(ValidationType.Email, EMAIL_REGEXP, value);

const RUSSIAN_REGEXP =
    // eslint-disable-next-line
    /^[А-Яа-яЁё\d\.,; \(\)_-]*$/;

export const validateRussian = (value: QuestionValue) =>
    validateByRegExp(ValidationType.Russian, RUSSIAN_REGEXP, value);

const INN_REGEXP =
    // eslint-disable-next-line
    /^(\d{10}|\d{12})$/;

export const validateINN = (value: QuestionValue) =>
    validateByRegExp(ValidationType.INN, INN_REGEXP, value);

const PHONE_REGEXP =
    // eslint-disable-next-line
    /^[+]?[0-9\s()-]{5,20}$/;

export const validatePhone = (value: QuestionValue) =>
    validateByRegExp(ValidationType.Phone, PHONE_REGEXP, value);
